/**
 * Optimizes image files in the browser before upload:
 * - Converts JPEG / PNG / large WebP to WebP with 88% quality.
 * - Scales down to max 2560px on the longest side (preserving aspect ratio).
 * - Leaves documents (PDF/Office) and videos untouched.
 */
export async function optimizeImageForUpload(
  file: File,
  options: { maxDimension?: number; quality?: number } = {},
): Promise<File> {
  const { maxDimension = 2560, quality = 0.88 } = options;

  // Only optimize raster images (exclude GIF animations and vector SVG)
  if (!file.type.startsWith('image/') || file.type === 'image/gif' || file.type === 'image/svg+xml') {
    return file;
  }

  try {
    const bitmap = await createImageBitmap(file);
    let { width, height } = bitmap;

    // If it's already WebP, within dimension limit and reasonably small (<500KB), keep original
    if (
      width <= maxDimension &&
      height <= maxDimension &&
      file.type === 'image/webp' &&
      file.size <= 500 * 1024
    ) {
      bitmap.close();
      return file;
    }

    // Scale down proportionally if larger than maxDimension
    if (width > maxDimension || height > maxDimension) {
      if (width > height) {
        height = Math.round((height * maxDimension) / width);
        width = maxDimension;
      } else {
        width = Math.round((width * maxDimension) / height);
        height = maxDimension;
      }
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) {
      bitmap.close();
      return file;
    }

    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/webp', quality);
    });

    if (!blob) return file;

    // Generate clean .webp filename
    const baseName = file.name.replace(/\.[^/.]+$/, '');
    const webpFileName = `${baseName}.webp`;

    return new File([blob], webpFileName, {
      type: 'image/webp',
      lastModified: Date.now(),
    });
  } catch {
    // If browser doesn't support canvas or image decoding fails, fallback to original
    return file;
  }
}
